import React from 'react'
import { Children } from './Children'
import {
  getBtnBG,
  getBtnBorder,
  getGradientBtnBG,
  GradientBtnTypes,
  NoGradientBtnTypes,
} from './aux'
import { ButtonProps } from './types'
import { useTheme } from '@/hooks/useTheme'

// Simple skeleton for button
const SkeletonButton: React.FC<{ width?: number }> = ({ width = 200 }) => (
  <div
    className="bg-gray-300 animate-pulse rounded-lg"
    style={{ width: `${width}px`, height: '48px' }}
  />
)

export const AppButton: React.FC<ButtonProps> = (props) => {
  const {
    onPress,
    isSharedSpace = false,
    style,
    skeletonActive = false,
    type = 'primaryGradient',
    isLoading = false,
    disabled,
    containerStyle,
    isContentRelative,
    size = 'large',
    iconGap = 12,
    iconName,
    skeletonWidth,
    className,
    labelProps: _labelProps, // Exclude from rest (used in Children)
    fontFamily: _fontFamily, // Exclude from rest (used in Children)
    height, // Exclude from rest for processing
    ...rest
  } = props

  const theme = useTheme()

  const isBlur = type === 'blur'
  const isPrimary = type === 'primaryGradient'
  const isContouredPurple = type === 'purpleGradientWithContour'
  const isGradient = isPrimary || isContouredPurple
  const isWarning = type === 'warning'

  const getIsTakesUpAllAvailableTheSpace = () => {
    if (isContentRelative) return false
    if (isSharedSpace) return true
    return false
  }

  const isTakesUpAllAvailableTheSpace = getIsTakesUpAllAvailableTheSpace()
  const border = getBtnBorder(type, theme, !!disabled)

  // Styles that cannot be replaced with Tailwind classes
  const inlineStyles: React.CSSProperties = {
    // Gap for icon (if present)
    ...(iconName && { gap: `${iconGap}px` }),

    // Button height (overrides size dimensions)
    ...(height && {
      height: typeof height === 'number' ? `${height}px` : height,
      minHeight: typeof height === 'number' ? `${height}px` : height,
    }),

    // Border (if present)
    ...(border.borderWidth > 0 && {
      borderWidth: `${border.borderWidth}px`,
      borderColor: border.borderColor,
      borderStyle: 'solid',
    }),

    // Container styles
    ...containerStyle,
  }

  // Gradient and color styles
  if (isGradient) {
    const gradientColors = getGradientBtnBG(
      type as GradientBtnTypes,
      theme,
      !!disabled,
    )
    inlineStyles.background = `linear-gradient(135deg, ${gradientColors.join(', ')})`
  } else {
    inlineStyles.backgroundColor = getBtnBG(
      type as NoGradientBtnTypes,
      theme,
      !!disabled,
    )
  }

  // Blur effect background (only if not overridden in style)
  // shouldnt the below be rgb(255 255 255 / 10%) ??
  if (isBlur && !style?.backgroundColor) {
    inlineStyles.backgroundColor = 'rgba(0, 0, 0, 0.1)'
  }
  if (isWarning) {
    inlineStyles.backgroundColor = 'rgba(243,44,62,1)'
  }

  // Apply custom styles last so they take priority
  Object.assign(inlineStyles, style)

  const handleClick = () => {
    if (!isLoading && !disabled && onPress) {
      // Telegram haptic feedback if available
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light')
      }
      onPress()
    }
  }

  if (skeletonActive) {
    return <SkeletonButton width={skeletonWidth} />
  }

  // Build className right here
  const baseClasses =
    'flex items-center justify-center rounded-2xl border-0 font-medium outline-none relative overflow-hidden transition-all duration-200 ease-in-out'
  const cursorClass =
    disabled || isLoading ? 'cursor-not-allowed' : 'cursor-pointer'

  // Sizes - if height is set, don't apply vertical padding from size
  const sizeClasses = height
    ? // If height is set, use only horizontal padding
      size === 'small'
      ? 'px-4 text-sm'
      : size === 'medium'
        ? 'px-6 text-base'
        : 'px-8 text-base' // large (default)
    : // If height is not set, use standard sizes
      size === 'small'
      ? 'px-4 py-2 text-sm'
      : size === 'medium'
        ? 'px-6 py-3 text-base'
        : 'px-8 py-4 text-base' // large (default)
  const widthClass = isContentRelative || isSharedSpace ? 'w-auto' : 'w-full'
  const flexClass = isTakesUpAllAvailableTheSpace ? 'flex-1' : ''
  const opacityClass = disabled ? 'opacity-60' : ''
  const blurClass = isBlur ? 'backdrop-blur-sm' : ''

  const combinedClassName = [
    baseClasses,
    cursorClass,
    sizeClasses,
    widthClass,
    flexClass,
    opacityClass,
    blurClass,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      style={inlineStyles}
      onClick={handleClick}
      disabled={disabled || isLoading}
      className={combinedClassName}
      {...rest}
    >
      <Children {...props} theme={theme} />
    </button>
  )
}
